import { RESERVATION_STATUS } from '@ibe/constants';
import { z } from 'zod';

/**
 * The Reservations module's contracts.
 *
 * The list row carries just enough for an operator to find the booking, decide
 * if it needs attention, and open it. The detail expands the stay, the guest,
 * the property and the items the reservation was sold on.
 */

export const reservationRow = z.object({
  id: z.string(),
  reference: z.string(),
  guestId: z.string(),
  guestName: z.string(),
  propertyId: z.string(),
  propertyName: z.string(),
  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.number(),
  status: z.string(),
  createdAt: z.string(),
  totalFormatted: z.string(),
});

export type ReservationRow = z.infer<typeof reservationRow>;

const reservationItem = z.object({
  id: z.string(),
  roomTypeId: z.string(),
  roomTypeName: z.string(),
  roomId: z.string().nullable(),
  ratePlanId: z.string().nullable(),
  ratePlanName: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.number(),
  adults: z.number(),
  children: z.number(),
  infants: z.number(),
  totalFormatted: z.string(),
});

export const reservationDetail = z.object({
  id: z.string(),
  reference: z.string(),
  guestId: z.string(),
  guestName: z.string(),
  propertyId: z.string(),
  propertyName: z.string(),
  status: z.string(),
  source: z.string(),
  pmsReservationId: z.string().nullable(),
  otaName: z.string().nullable(),
  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.number(),
  rooms: z.number(),
  adults: z.number(),
  children: z.number(),
  infants: z.number(),
  specialRequests: z.string().nullable(),
  currency: z.string(),
  totalFormatted: z.string(),
  createdAt: z.string(),
  items: z.array(reservationItem),
});

export type ReservationDetail = z.infer<typeof reservationDetail>;
export type ReservationItem = z.infer<typeof reservationItem>;

export const RESERVATION_STATUS_OPTIONS: readonly { value: string; label: string }[] = [
  { value: RESERVATION_STATUS.DRAFT, label: 'Draft' },
  { value: RESERVATION_STATUS.INQUIRY, label: 'Inquiry' },
  { value: RESERVATION_STATUS.PENDING, label: 'Pending' },
  { value: RESERVATION_STATUS.PENCIL, label: 'Pencil' },
  { value: RESERVATION_STATUS.CONFIRMED, label: 'Confirmed' },
  { value: RESERVATION_STATUS.MODIFIED, label: 'Modified' },
  { value: RESERVATION_STATUS.PRECHECKED_IN, label: 'Pre-checked in' },
  { value: RESERVATION_STATUS.PARTIALLY_PRECHECKED_IN, label: 'Partially pre-checked in' },
  { value: RESERVATION_STATUS.CHECKED_IN, label: 'Checked in' },
  { value: RESERVATION_STATUS.CHECKED_OUT, label: 'Checked out' },
  { value: RESERVATION_STATUS.CANCELLED, label: 'Cancelled' },
  { value: RESERVATION_STATUS.NO_SHOW, label: 'No show' },
  { value: RESERVATION_STATUS.EXPIRED, label: 'Expired' },
  { value: RESERVATION_STATUS.MAINTENANCE, label: 'Maintenance' },
];
